import React from 'react';
import { SidePanelClose, SidePanelOpen } from '@carbon/icons-react';
import {
  CoreLogo,
  IconButton,
  SidebarNav,
  SidebarNavFavorites,
  SidebarNavGroup,
  SidebarNavItem,
  Skeleton,
  Tag,
} from '@ditioas/web-ui';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';
import { useMedia } from 'use-media';

import config from 'config/site.config';
import {
  useGetMenuContentV2,
  useGetMenuPreferences,
  useSaveMenuPreferences,
  useSetMenuPreferencesCache,
} from 'data/menu-preferences-api-hooks';
import type { MenuContent, MenuPage, MenuPreferences } from 'model/Menu/types';

import { navMenuStateAtom, sidebarBreakpoint } from '../Layout';
import getLinkIcon from '../PageLink/getLinkIcon';

// Collapse state is persisted server-side (menu preferences) under a stable,
// language-independent group key — `sectionName` is translated, so keying by it
// would reset collapse state on every language switch. Grouped (v2) sections
// carry `sectionKey` from the backend; unmapped leftover sections fall back to a
// page-name signature (page `name`s are stable module ids), namespaced so it can
// never collide with the Favorites group.
const favoritesGroupKey = 'favorites';

const getSectionKey = (section: MenuContent[number]): string =>
  section.sectionKey ??
  `section:${section.pages.map((page) => page.name).join('|')}`;

const SidebarNavV2 = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useAtom(navMenuStateAtom);
  const rail = !menuOpen;
  const isWide = useMedia({ minWidth: parseInt(sidebarBreakpoint, 10) }, true);

  const { data: menuSections } = useGetMenuContentV2();
  const { data: preferences } = useGetMenuPreferences();
  const saveMenuPreferences = useSaveMenuPreferences();
  const setMenuPreferencesCache = useSetMenuPreferencesCache();

  // Follow the persisted rail state until the user toggles it themselves this
  // session. Keyed on the value (not a run-once flag) because the SWR cache
  // seed may predate the field — the fresh fetch must still apply it. Never
  // applied on narrow screens: Layout collapses the menu there, and the
  // persisted "expanded" state must not re-open a sidebar that squeezes the
  // whole page and has no overlay dismiss.
  const userToggledRail = React.useRef(false);
  const persistedSidebarCollapsed = preferences?.sidebarCollapsed;
  React.useEffect(() => {
    if (
      !isWide ||
      persistedSidebarCollapsed === undefined ||
      userToggledRail.current
    )
      return;

    setMenuOpen(!persistedSidebarCollapsed);
  }, [persistedSidebarCollapsed, isWide, setMenuOpen]);

  // Below the breakpoint the in-flow sidebar competes with the page for width
  // and has no overlay, so collapse it on every navigation — the old menu's
  // PageLink did the same for narrow screens.
  React.useEffect(() => {
    if (!isWide) setMenuOpen(false);
  }, [pathname, isWide, setMenuOpen]);

  const favorites = React.useMemo(
    () => preferences?.favoriteModules ?? [],
    [preferences]
  );
  const collapsedSections = React.useMemo(
    () => preferences?.collapsedSections ?? [],
    [preferences]
  );

  const pagesByName = React.useMemo(() => {
    const map = new Map<string, MenuPage>();
    menuSections?.forEach((section) =>
      section.pages.forEach((page) => map.set(page.name, page))
    );

    return map;
  }, [menuSections]);

  // Every save guards on `preferences` being loaded — the PUT replaces the whole
  // document, so saving before the GET resolves would wipe the user's data.
  const handleToggleGroup = (groupKey: string, collapsed: boolean): void => {
    if (!preferences) return;

    const next = collapsed
      ? [...collapsedSections, groupKey]
      : collapsedSections.filter((key) => key !== groupKey);
    saveMenuPreferences({ ...preferences, collapsedSections: next });
  };

  const handleToggleFavorite = (name: string): void => {
    if (!preferences) return;

    const next = favorites.includes(name)
      ? favorites.filter((favorite) => favorite !== name)
      : [...favorites, name];
    saveMenuPreferences({
      ...preferences,
      favoriteModules: next,
      useNewMenu: true,
    });
  };

  // The drag emits one onReorder per row crossed. Reflect each step in the
  // local SWR cache immediately (the rows must re-render mid-drag) but debounce
  // the PUT to one per gesture — per-step PUTs are concurrent whole-document
  // writes whose server-side arrival order is not guaranteed.
  const reorderFlush = React.useRef<{
    timer: number | null;
    pending: MenuPreferences | null;
  }>({ timer: null, pending: null });

  React.useEffect(() => {
    const flush = reorderFlush.current;

    return () => {
      // Unmount mid-debounce: persist the last order instead of dropping it.
      if (flush.timer !== null) window.clearTimeout(flush.timer);
      if (flush.pending) saveMenuPreferences(flush.pending);
    };
  }, []);

  const handleReorderFavorites = (fromIndex: number, toIndex: number): void => {
    if (!preferences || fromIndex === toIndex) return;

    // Drag indices refer to the RENDERED rows, which exclude favorites hidden
    // in the current company — map them back to positions in the full stored
    // list before splicing, or the first drag after a company switch silently
    // reorders (and persists) the wrong entry.
    const visibleIndices = favorites
      .map((name, index) => (pagesByName.has(name) ? index : -1))
      .filter((index) => index !== -1);
    const from = visibleIndices[fromIndex];
    const to = visibleIndices[toIndex];
    if (from === undefined || to === undefined) return;

    const next = [...favorites];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    const nextPreferences = {
      ...preferences,
      favoriteModules: next,
      useNewMenu: true,
    };

    setMenuPreferencesCache(nextPreferences);
    const flush = reorderFlush.current;
    flush.pending = nextPreferences;
    if (flush.timer !== null) window.clearTimeout(flush.timer);
    flush.timer = window.setTimeout(() => {
      flush.timer = null;
      flush.pending = null;
      saveMenuPreferences(nextPreferences);
    }, 400);
  };

  const handleSwitchBack = (): void => {
    if (!preferences) return;

    saveMenuPreferences({ ...preferences, useNewMenu: false });
  };

  const renderItem = (page: MenuPage, favoritable = true): React.ReactNode => {
    const icon = getLinkIcon(page.name);
    const isExternal = /^https?:\/\//i.test(page.link);
    // Segment-boundary match so `/time` doesn't light up for `/timesheet`.
    const active =
      !isExternal &&
      !page.isLegacy &&
      (pathname === `/${page.link}` || pathname.startsWith(`/${page.link}/`));

    const favoriteProps = favoritable
      ? {
          favorited: favorites.includes(page.name),
          onFavoriteToggle: () => handleToggleFavorite(page.name),
          favoriteToggleAriaLabel: t('Menu.PinToFavorites', {
            title: page.title,
          }),
        }
      : {};

    if (isExternal) {
      return (
        <SidebarNavItem
          key={page.name}
          asChild
          icon={icon}
          label={page.title}
          external
          {...favoriteProps}
        >
          <a href={page.link} aria-label={page.title} />
        </SidebarNavItem>
      );
    }

    if (page.isLegacy) {
      return (
        <SidebarNavItem
          key={page.name}
          asChild
          icon={icon}
          label={page.title}
          {...favoriteProps}
        >
          <a
            href={`${config.legacyWebBaseUrl}/${page.link}`}
            aria-label={page.title}
          />
        </SidebarNavItem>
      );
    }

    // No special mass-transport branch: the backend sends Mass Haul (and Flow)
    // with absolute URLs, so the isExternal branch above already routes them.
    // The previous branch here dereferenced config.modules.masstransport, which
    // does not exist — it would have thrown for every internal page as soon as
    // the masstransport env var was set (test / prod).

    return (
      <SidebarNavItem
        key={page.name}
        asChild
        icon={icon}
        label={page.title}
        active={active}
        {...favoriteProps}
      >
        <Link to={`../${page.link}`} />
      </SidebarNavItem>
    );
  };

  const footerSection = menuSections?.find((section) => section.isFooter);
  const mainSections = menuSections?.filter((section) => !section.isFooter);

  return (
    <SidebarNav
      rail={rail}
      header={
        <>
          {!rail && (
            <a href={config.legacyWebBaseUrl}>
              <p className="sr-only">Ditio</p>
              <CoreLogo variant="brand" />
            </a>
          )}
          <IconButton
            data-testid="sidebar-nav-v2.rail-toggle"
            variant="clear"
            shape="square"
            size="sm"
            aria-label={rail ? t('Menu.ExpandMenu') : t('Menu.CollapseMenu')}
            onClick={() => {
              userToggledRail.current = true;
              setMenuOpen(rail);
              // Persist so the rail state follows the user to the legacy web
              // and back; guarded on `preferences` like every other save.
              if (preferences) {
                saveMenuPreferences({
                  ...preferences,
                  sidebarCollapsed: !rail,
                });
              }
            }}
          >
            {rail ? <SidePanelOpen /> : <SidePanelClose />}
          </IconButton>
        </>
      }
      footer={
        <>
          {!rail && (
            <div className="bg-azure-100 dark:bg-azure-900/40 mx-3 mb-2 flex items-center gap-2 rounded-lg px-3 py-2">
              <Tag size="xs" color="azure" text="BETA" className="shrink-0" />
              <span className="text-sand-1000 dark:text-dark-1000 flex-1 text-[11px] leading-tight">
                {t('Menu.TestingNewMenu')}
              </span>
              <button
                type="button"
                data-testid="sidebar-nav-v2.switch-back"
                onClick={handleSwitchBack}
                className="text-azure-700 body-xxs ml-auto shrink-0 font-medium hover:underline"
              >
                {t('Menu.SwitchBack')}
              </button>
            </div>
          )}
          {footerSection?.pages.map((page) => renderItem(page, false))}
        </>
      }
    >
      {mainSections ? (
        <>
          <SidebarNavFavorites
            label={t('Menu.Favorites')}
            emptyHint={t('Menu.FavoritesEmptyHint')}
            collapsed={collapsedSections.includes(favoritesGroupKey)}
            onCollapsedChange={(collapsed) =>
              handleToggleGroup(favoritesGroupKey, collapsed)
            }
            onReorder={handleReorderFavorites}
          >
            {favorites
              .map((name) => pagesByName.get(name))
              .filter((page): page is MenuPage => Boolean(page))
              .map((page) => renderItem(page))}
          </SidebarNavFavorites>

          {mainSections.map((section) => {
            const sectionKey = getSectionKey(section);
            // Favorited pages move to the Favorites group; a section whose
            // every page is pinned would render as an empty labeled header,
            // so hide it — it comes back as soon as one page is unpinned.
            const remainingPages = section.pages.filter(
              (page) => !favorites.includes(page.name)
            );
            if (remainingPages.length === 0) return null;

            return (
              <SidebarNavGroup
                key={sectionKey}
                label={section.sectionName}
                collapsed={collapsedSections.includes(sectionKey)}
                onCollapsedChange={(collapsed) =>
                  handleToggleGroup(sectionKey, collapsed)
                }
              >
                {remainingPages.map((page) => renderItem(page))}
              </SidebarNavGroup>
            );
          })}
        </>
      ) : (
        // match the real menu's fixed row width, height and spacing:
        // full-width h-9 rows grouped like the sections, filling the menu
        <div className="flex flex-col gap-2">
          {[3, 4, 3, 5, 5, 3].map((rowCount, groupIndex) => (
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={`sidebar-nav-skeleton-group-${groupIndex}`}
              className="flex flex-col gap-0.5"
            >
              <Skeleton className="mb-1 ml-3 h-3 w-22" />
              {Array(rowCount)
                .fill(null)
                .map((_, rowIndex) => (
                  <Skeleton
                    // eslint-disable-next-line react/no-array-index-key
                    key={`sidebar-nav-skeleton-${groupIndex}-${rowIndex}`}
                    className="h-9 w-full"
                  />
                ))}
            </div>
          ))}
        </div>
      )}
    </SidebarNav>
  );
};

export default SidebarNavV2;
